import { ArrowLeftOutlined, PlusOutlined } from "@ant-design/icons";
import { Button, Tag } from "antd/es";
import { useState, useEffect, useMemo } from "react";
import { findRunArgs, getWasabiFile } from "../../api";
import { recursiveObjectSort } from "../../util";

import CollapsingCard from "../CollapsingCard";
import { analyzeLosses } from "./ModelStorageBrowser";
import { ImageLosses } from "./panels/ImageLosses";
import { OverallLossPerTimestep } from "./panels/OverallLossPerTimestep";
import { TimestepSamplingDistribution } from "./panels/TimestepSamplingDistribution";
import { NormalizedImageLosses } from "./panels/NormalizedImageLosses";
import { EvalImages } from "./panels/EvalImages";
import { RuntimeChanges } from "./panels/RuntimeChanges";

export const ModelDetails = (props) => {
    const { selectedModel, close, derive } = props;
    const [modelArgs, setModelArgs] = useState(null);
    const [captions, setCaptions] = useState(null);
    const [lossFile, setLossFile] = useState(null);

    const path = selectedModel.path.split("/checkpoint-")[0];
    const step = Number(selectedModel.path.split("checkpoint-")[1].split("/")[0]);

    useEffect(() => {
        if (selectedModel) {
            findRunArgs("projects/pixels-randomized-coa2/" + path, 1).then(args => {
                setModelArgs(recursiveObjectSort(args));

                getWasabiFile(`models/coa-finetune/${path.substr(2)}/captions.txt`).then(captions => {
                    setCaptions(captions);
                });

                getWasabiFile(`models/coa-finetune/${path.substr(2)}/image-loss.txt`).then(lossFile => {
                    setLossFile(lossFile);
                });
            });
        }
    }, [selectedModel, path, step]);

    const { losses, relative, overall, averages, timestepFrequencies } = useMemo(() => {
        if (!lossFile) {
            return { losses: null, relative: null, overall: null, averages: null, timestepFrequencies: null };
        }

        const lines = lossFile.split('\n').map(l => l.trim()).filter(l => l.length);
        const data = analyzeLosses(lines);

        return data;
    }, [lossFile]);


    const files = useMemo(() => Object.keys(losses ?? []).sort((a, b) => relative[b] - relative[a]), [losses, relative]);
    const imageNames = useMemo(() => (files ?? []).toSorted().map(f => ({
        label: f.split('/').at(-1),
        value: f,
    })), [files]);

    return (
        <div className="off-canvas-content">
            <div className="off-canvas-header">
                <Button type="default" size="small" icon={<ArrowLeftOutlined />} onClick={() => close()} />
                <div>{path} <Tag>{step}</Tag></div>
            </div>

            <div className="off-canvas-body" style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                <CollapsingCard title="Model details">
                    <pre>{selectedModel ? JSON.stringify(selectedModel, null, 4) : '...'}</pre>
                </CollapsingCard>

                <CollapsingCard title="Invocation args" defaultCollapsed>
                    <Button type={"primary"} color="" onClick={() => derive(modelArgs.current_status.args)} disabled={!modelArgs} icon={<PlusOutlined />}>Create training...</Button>
                    <Button type={"primary"} color="" onClick={() => derive({ ...modelArgs.current_status.args, continue_from: `${path.substr(2)}/checkpoint-${step}` })} disabled={!modelArgs} icon={<PlusOutlined />}>Create training...</Button>

                    <pre style={{ maxHeight: 250, overflowY: 'auto' }}>{modelArgs ? JSON.stringify(modelArgs, null, 4) : '...'}</pre>
                </CollapsingCard>

                <CollapsingCard title="Runtime argument changes" defaultCollapsed>
                    {modelArgs && <RuntimeChanges modelArgs={modelArgs} />}
                </CollapsingCard>

                <CollapsingCard title="Loss per timestep" defaultCollapsed>
                    {lossFile && <OverallLossPerTimestep overall={overall} losses={losses} />}
                </CollapsingCard>

                <CollapsingCard title="Timestep sampling distribution" defaultCollapsed>
                    {lossFile && <TimestepSamplingDistribution frequencies={timestepFrequencies ?? []} averages={averages} losses={losses} overall={overall} />}
                </CollapsingCard>

                <CollapsingCard title="Per-image (normalized) training loss" defaultCollapsed>
                    {lossFile && <NormalizedImageLosses losses={losses} relative={relative} overall={overall} files={files} imageNames={imageNames} averages={averages} />}
                </CollapsingCard>

                <CollapsingCard title="Per-image loss analysis" defaultCollapsed>
                    {lossFile && <ImageLosses losses={losses} relative={relative} overall={overall} files={files} imageNames={imageNames} captions={captions ?? ''} />}
                </CollapsingCard>

                <CollapsingCard title="Evaluation results" defaultCollapsed>
                    <EvalImages model={selectedModel} />
                </CollapsingCard>

                <CollapsingCard title="Captions" defaultCollapsed>
                    {modelArgs ? <>
                        Prefix:
                        <pre>{modelArgs.current_status.args.caption_prefix ?? <i>None</i>}</pre>
                        Suffix:
                        <pre>{modelArgs.current_status.args.caption_suffix ?? <i>None</i>}</pre>
                    </> : null}

                    Captions file:
                    <pre>{captions ?? '...'}</pre>
                </CollapsingCard>
            </div>
        </div>
    );
};
